import { z } from "zod";

export type Workspace = "webmaster" | "advertiser" | "admin";

export const workspaceSummarySchema = z.object({
  workspace: z.enum(["webmaster", "advertiser", "admin"]),
  health: z.enum(["ok", "degraded"]),
  metrics: z.array(
    z.object({
      id: z.string(),
      labelKey: z.string(),
      value: z.string(),
      delta: z.string()
    })
  ),
  rows: z.array(
    z.object({
      id: z.string(),
      title: z.string(),
      statusKey: z.string(),
      amount: z.string()
    })
  )
});

export type WorkspaceSummary = z.infer<typeof workspaceSummarySchema>;
export type ActorRole = "platform_admin" | "platform_support";
export type EntityStatus = "active" | "review" | "paused";

export type Session = {
  email: string;
  accessToken: string;
  accessExpiresAt: string;
  refreshToken: string;
  refreshKID: string;
  refreshExpiresAt: string;
  tokenType: "Bearer";
  userID?: string;
  actorRole?: ActorRole;
  workspace?: Workspace;
};

const sessionSchema = z.object({
  email: z.string(),
  accessToken: z.string(),
  accessExpiresAt: z.string(),
  refreshToken: z.string(),
  refreshKID: z.string(),
  refreshExpiresAt: z.string(),
  tokenType: z.literal("Bearer"),
  userID: z.string().optional(),
  actorRole: z.enum(["platform_admin", "platform_support"]).optional(),
  workspace: z.enum(["webmaster", "advertiser", "admin"]).optional()
});

const sessionResponseSchema = z.object({
  status: z.literal("ok"),
  token_type: z.literal("Bearer"),
  access_token: z.string(),
  access_expires_at: z.string(),
  refresh_token: z.string(),
  refresh_kid: z.string(),
  refresh_expires_at: z.string()
});

const sessionProfileSchema = z.object({
  subject: z.string(),
  actor_role: z.enum(["platform_admin", "platform_support"]),
  default_workspace: z.enum(["webmaster", "advertiser", "admin"]),
  workspaces: z.array(z.enum(["webmaster", "advertiser", "admin"]))
});

export type SessionProfile = z.infer<typeof sessionProfileSchema>;

export const bulkLeadJobSchema = z.object({
  id: z.string(),
  type: z.enum(["import", "export"]),
  status: z.enum(["queued", "running", "succeeded", "failed"]),
  totalRows: z.number().int().nonnegative(),
  processedRows: z.number().int().nonnegative(),
  failedRows: z.number().int().nonnegative()
});

export type BulkLeadJob = z.infer<typeof bulkLeadJobSchema>;

export const adminUserSchema = z.object({
  id: z.string(),
  email: z.string(),
  role: z.string()
});

export const adminUsersResponseSchema = z.object({
  items: z.array(adminUserSchema),
  can_delete: z.boolean(),
  actor_role: z.string(),
  total_users: z.number().int().nonnegative()
});

export type AdminUser = z.infer<typeof adminUserSchema>;
export type AdminUsersResponse = z.infer<typeof adminUsersResponseSchema>;

export const workspaceEntitySchema = z.object({
  id: z.string(),
  workspace: z.enum(["webmaster", "advertiser", "admin"]),
  entity: z.string(),
  name: z.string(),
  status: z.enum(["active", "review", "paused"]),
  owner: z.string(),
  metric: z.string(),
  updated_at: z.string(),
  fields: z.record(z.string(), z.string())
});

export const workspaceEntitiesResponseSchema = z.object({
  items: z.array(workspaceEntitySchema)
});

export type WorkspaceEntity = z.infer<typeof workspaceEntitySchema>;
export type WorkspaceEntityPayload = {
  name: string;
  status: EntityStatus;
  owner: string;
  metric: string;
  fields: Record<string, string>;
};

const bffBase = "/bff/ui";
const workspaceMVPBase = `${bffBase}/workspace-mvp`;
const sessionStorageKey = "leadstack_admin_session";
const legacyLocalStorageSessionKey = "leadstack_admin_session";

const workspaceMVPDateTimeSchema = z.string();
const workspaceMVPWorkspaceSchema = z.enum(["webmaster", "advertiser", "admin"]);
const workspaceMVPOfferStatusSchema = z.enum(["active", "review", "paused", "archived"]);
const workspaceMVPLeadStatusSchema = z.enum(["new", "validated", "accepted", "rejected", "hold"]);
const workspaceMVPOrderStatusSchema = z.enum(["pending", "approved", "cancelled", "refunded"]);
const workspaceMVPPostbackStatusSchema = z.enum(["active", "paused", "failed"]);
const workspaceMVPPayoutStatusSchema = z.enum(["pending", "approved", "paid", "rejected"]);
const workspaceMVPUserStatusSchema = z.enum(["active", "invited", "suspended"]);
const workspaceMVPCompanyStatusSchema = z.enum(["active", "review", "suspended"]);
const workspaceMVPUserRoleSchema = z.enum([
  "platform_admin",
  "platform_support",
  "advertiser_owner",
  "advertiser_manager",
  "webmaster"
]);
const workspaceMVPPayoutMethodSchema = z.enum(["manual", "bank_transfer", "crypto"]);

export const workspaceMVPOfferSchema = z.object({
  id: z.string(),
  workspace: workspaceMVPWorkspaceSchema,
  company_id: z.string(),
  name: z.string(),
  status: workspaceMVPOfferStatusSchema,
  geo: z.array(z.string()),
  payout_cents: z.number().int().nonnegative(),
  currency: z.string(),
  created_at: workspaceMVPDateTimeSchema,
  updated_at: workspaceMVPDateTimeSchema
});

export const workspaceMVPOfferPayloadSchema = workspaceMVPOfferSchema.omit({
  id: true,
  created_at: true,
  updated_at: true
});

export const workspaceMVPFlowSchema = z.object({
  id: z.string(),
  workspace: workspaceMVPWorkspaceSchema,
  offer_id: z.string(),
  name: z.string(),
  status: workspaceMVPOfferStatusSchema,
  traffic_source: z.string(),
  landing_url: z.string(),
  daily_cap: z.number().int().nonnegative(),
  created_at: workspaceMVPDateTimeSchema,
  updated_at: workspaceMVPDateTimeSchema
});

export const workspaceMVPFlowPayloadSchema = workspaceMVPFlowSchema.omit({
  id: true,
  created_at: true,
  updated_at: true
});

export const workspaceMVPLeadSchema = z.object({
  id: z.string(),
  workspace: workspaceMVPWorkspaceSchema,
  offer_id: z.string(),
  flow_id: z.string(),
  customer_ref: z.string(),
  country: z.string(),
  source: z.string(),
  status: workspaceMVPLeadStatusSchema,
  created_at: workspaceMVPDateTimeSchema,
  updated_at: workspaceMVPDateTimeSchema
});

export const workspaceMVPLeadPayloadSchema = workspaceMVPLeadSchema.omit({
  id: true,
  created_at: true,
  updated_at: true
});

export const workspaceMVPOrderSchema = z.object({
  id: z.string(),
  lead_id: z.string(),
  offer_id: z.string(),
  status: workspaceMVPOrderStatusSchema,
  amount_cents: z.number().int().nonnegative(),
  currency: z.string(),
  created_at: workspaceMVPDateTimeSchema,
  updated_at: workspaceMVPDateTimeSchema
});

export const workspaceMVPOrderPayloadSchema = workspaceMVPOrderSchema.omit({
  id: true,
  offer_id: true,
  created_at: true,
  updated_at: true
});

export const workspaceMVPPostbackSchema = z.object({
  id: z.string(),
  workspace: workspaceMVPWorkspaceSchema,
  flow_id: z.string(),
  event: z.string(),
  target_url: z.string(),
  status: workspaceMVPPostbackStatusSchema,
  created_at: workspaceMVPDateTimeSchema,
  updated_at: workspaceMVPDateTimeSchema
});

export const workspaceMVPPostbackPayloadSchema = workspaceMVPPostbackSchema.omit({
  id: true,
  created_at: true,
  updated_at: true
});

export const workspaceMVPPayoutSchema = z.object({
  id: z.string(),
  workspace: workspaceMVPWorkspaceSchema,
  user_id: z.string(),
  status: workspaceMVPPayoutStatusSchema,
  amount_cents: z.number().int().positive(),
  currency: z.string(),
  method: workspaceMVPPayoutMethodSchema,
  created_at: workspaceMVPDateTimeSchema,
  updated_at: workspaceMVPDateTimeSchema
});

export const workspaceMVPPayoutPayloadSchema = workspaceMVPPayoutSchema.omit({
  id: true,
  created_at: true,
  updated_at: true
});

export const workspaceMVPUserSchema = z.object({
  id: z.string(),
  email: z.string(),
  display_name: z.string(),
  role: workspaceMVPUserRoleSchema,
  company_id: z.string(),
  status: workspaceMVPUserStatusSchema,
  workspaces: z.array(workspaceMVPWorkspaceSchema),
  created_at: workspaceMVPDateTimeSchema,
  updated_at: workspaceMVPDateTimeSchema
});

export const workspaceMVPUserPayloadSchema = workspaceMVPUserSchema.omit({
  id: true,
  created_at: true,
  updated_at: true
});

export const workspaceMVPCompanySchema = z.object({
  id: z.string(),
  name: z.string(),
  status: workspaceMVPCompanyStatusSchema,
  plan: z.string(),
  created_at: workspaceMVPDateTimeSchema,
  updated_at: workspaceMVPDateTimeSchema
});

export const workspaceMVPCompanyPayloadSchema = workspaceMVPCompanySchema.omit({
  id: true,
  created_at: true,
  updated_at: true
});

export const workspaceMVPStatsSchema = z.object({
  generated_at: workspaceMVPDateTimeSchema,
  counts: z.record(z.string(), z.number().int().nonnegative()),
  lead_statuses: z.record(z.string(), z.number().int().nonnegative()),
  order_statuses: z.record(z.string(), z.number().int().nonnegative()),
  payout_statuses: z.record(z.string(), z.number().int().nonnegative())
});

const workspaceMVPOffersResponseSchema = z.object({ items: z.array(workspaceMVPOfferSchema) });
const workspaceMVPFlowsResponseSchema = z.object({ items: z.array(workspaceMVPFlowSchema) });
const workspaceMVPLeadsResponseSchema = z.object({ items: z.array(workspaceMVPLeadSchema) });
const workspaceMVPOrdersResponseSchema = z.object({ items: z.array(workspaceMVPOrderSchema) });
const workspaceMVPPostbacksResponseSchema = z.object({ items: z.array(workspaceMVPPostbackSchema) });
const workspaceMVPPayoutsResponseSchema = z.object({ items: z.array(workspaceMVPPayoutSchema) });
const workspaceMVPUsersResponseSchema = z.object({ items: z.array(workspaceMVPUserSchema) });
const workspaceMVPCompaniesResponseSchema = z.object({ items: z.array(workspaceMVPCompanySchema) });

export type WorkspaceMVPWorkspace = z.infer<typeof workspaceMVPWorkspaceSchema>;
export type WorkspaceMVPOfferStatus = z.infer<typeof workspaceMVPOfferStatusSchema>;
export type WorkspaceMVPLeadStatus = z.infer<typeof workspaceMVPLeadStatusSchema>;
export type WorkspaceMVPOrderStatus = z.infer<typeof workspaceMVPOrderStatusSchema>;
export type WorkspaceMVPPostbackStatus = z.infer<typeof workspaceMVPPostbackStatusSchema>;
export type WorkspaceMVPPayoutStatus = z.infer<typeof workspaceMVPPayoutStatusSchema>;
export type WorkspaceMVPUserStatus = z.infer<typeof workspaceMVPUserStatusSchema>;
export type WorkspaceMVPCompanyStatus = z.infer<typeof workspaceMVPCompanyStatusSchema>;
export type WorkspaceMVPUserRole = z.infer<typeof workspaceMVPUserRoleSchema>;
export type WorkspaceMVPPayoutMethod = z.infer<typeof workspaceMVPPayoutMethodSchema>;

export type WorkspaceMVPOffer = z.infer<typeof workspaceMVPOfferSchema>;
export type WorkspaceMVPOfferPayload = z.infer<typeof workspaceMVPOfferPayloadSchema>;
export type WorkspaceMVPFlow = z.infer<typeof workspaceMVPFlowSchema>;
export type WorkspaceMVPFlowPayload = z.infer<typeof workspaceMVPFlowPayloadSchema>;
export type WorkspaceMVPLead = z.infer<typeof workspaceMVPLeadSchema>;
export type WorkspaceMVPLeadPayload = z.infer<typeof workspaceMVPLeadPayloadSchema>;
export type WorkspaceMVPOrder = z.infer<typeof workspaceMVPOrderSchema>;
export type WorkspaceMVPOrderPayload = z.infer<typeof workspaceMVPOrderPayloadSchema>;
export type WorkspaceMVPPostback = z.infer<typeof workspaceMVPPostbackSchema>;
export type WorkspaceMVPPostbackPayload = z.infer<typeof workspaceMVPPostbackPayloadSchema>;
export type WorkspaceMVPPayout = z.infer<typeof workspaceMVPPayoutSchema>;
export type WorkspaceMVPPayoutPayload = z.infer<typeof workspaceMVPPayoutPayloadSchema>;
export type WorkspaceMVPUser = z.infer<typeof workspaceMVPUserSchema>;
export type WorkspaceMVPUserPayload = z.infer<typeof workspaceMVPUserPayloadSchema>;
export type WorkspaceMVPCompany = z.infer<typeof workspaceMVPCompanySchema>;
export type WorkspaceMVPCompanyPayload = z.infer<typeof workspaceMVPCompanyPayloadSchema>;
export type WorkspaceMVPStats = z.infer<typeof workspaceMVPStatsSchema>;

export type WorkspaceMVPWorkspaceFilter = {
  workspace?: WorkspaceMVPWorkspace;
};

export type WorkspaceMVPOfferFilter = WorkspaceMVPWorkspaceFilter & {
  status?: WorkspaceMVPOfferStatus;
  company_id?: string;
};

export type WorkspaceMVPFlowFilter = WorkspaceMVPWorkspaceFilter & {
  status?: WorkspaceMVPOfferStatus;
  offer_id?: string;
};

export type WorkspaceMVPLeadFilter = WorkspaceMVPWorkspaceFilter & {
  status?: WorkspaceMVPLeadStatus;
  offer_id?: string;
  flow_id?: string;
};

export type WorkspaceMVPOrderFilter = {
  status?: WorkspaceMVPOrderStatus;
  offer_id?: string;
  lead_id?: string;
};

export type WorkspaceMVPPostbackFilter = WorkspaceMVPWorkspaceFilter & {
  status?: WorkspaceMVPPostbackStatus;
  flow_id?: string;
};

export type WorkspaceMVPPayoutFilter = WorkspaceMVPWorkspaceFilter & {
  status?: WorkspaceMVPPayoutStatus;
  user_id?: string;
};

export type WorkspaceMVPUserFilter = {
  workspace?: WorkspaceMVPWorkspace;
  status?: WorkspaceMVPUserStatus;
  company_id?: string;
  role?: WorkspaceMVPUserRole;
};

export type WorkspaceMVPCompanyFilter = {
  status?: WorkspaceMVPCompanyStatus;
};

export async function login(email: string, password: string): Promise<Session> {
  const payload = await postPublicJSON("/bff/api/v1/auth/session/login", { email, password });
  const parsed = sessionResponseSchema.parse(payload);
  return persistSession({
    email,
    accessToken: parsed.access_token,
    accessExpiresAt: parsed.access_expires_at,
    refreshToken: parsed.refresh_token,
    refreshKID: parsed.refresh_kid,
    refreshExpiresAt: parsed.refresh_expires_at,
    tokenType: parsed.token_type
  });
}

export async function fetchSessionProfile(): Promise<SessionProfile> {
  const response = await authFetch(`${bffBase}/session/me`);
  return sessionProfileSchema.parse(await response.json());
}

export async function restoreSession(): Promise<Session | null> {
  const session = loadSession();
  if (!session) {
    return null;
  }

  try {
    const profile = await fetchSessionProfile();
    return persistSession({
      ...session,
      actorRole: profile.actor_role,
      workspace: profile.default_workspace,
      userID: profile.subject
    });
  } catch {
    clearSession();
    return null;
  }
}

export function loadSession(): Session | null {
  window.localStorage.removeItem(legacyLocalStorageSessionKey);
  const raw = window.sessionStorage.getItem(sessionStorageKey);
  if (!raw) {
    return null;
  }
  try {
    const session = sessionSchema.parse(JSON.parse(raw));
    if (Date.parse(session.refreshExpiresAt) <= Date.now()) {
      clearSession();
      return null;
    }
    return session;
  } catch {
    clearSession();
    return null;
  }
}

export function persistSession(session: Session): Session {
  window.localStorage.removeItem(legacyLocalStorageSessionKey);
  window.sessionStorage.setItem(sessionStorageKey, JSON.stringify(session));
  return session;
}

export function clearSession(): void {
  window.localStorage.removeItem(legacyLocalStorageSessionKey);
  window.sessionStorage.removeItem(sessionStorageKey);
  window.localStorage.removeItem("leadstack_actor_role");
}

export async function fetchWorkspaceSummary(workspace: Workspace): Promise<WorkspaceSummary> {
  const response = await authFetch(`${bffBase}/workspaces/${workspace}`);
  return workspaceSummarySchema.parse(await response.json());
}

export async function fetchWorkspaceEntities(workspace: Workspace, entity: string): Promise<WorkspaceEntity[]> {
  const response = await authFetch(`${bffBase}/entities/${workspace}/${entity}`);
  return workspaceEntitiesResponseSchema.parse(await response.json()).items;
}

export async function createWorkspaceEntity(
  workspace: Workspace,
  entity: string,
  payload: WorkspaceEntityPayload
): Promise<WorkspaceEntity> {
  const response = await authFetch(`${bffBase}/entities/${workspace}/${entity}`, {
    method: "POST",
    body: JSON.stringify(payload)
  });
  return workspaceEntitySchema.parse(await response.json());
}

export async function updateWorkspaceEntity(
  item: WorkspaceEntity,
  payload: WorkspaceEntityPayload
): Promise<WorkspaceEntity> {
  const response = await authFetch(`${bffBase}/entities/${item.workspace}/${item.entity}/${encodeURIComponent(item.id)}`, {
    method: "PUT",
    body: JSON.stringify(payload)
  });
  return workspaceEntitySchema.parse(await response.json());
}

export async function deleteWorkspaceEntity(item: WorkspaceEntity): Promise<void> {
  await authFetch(`${bffBase}/entities/${item.workspace}/${item.entity}/${encodeURIComponent(item.id)}`, {
    method: "DELETE"
  });
}

export async function fetchBulkLeadJobs(): Promise<BulkLeadJob[]> {
  const response = await authFetch(`${bffBase}/leads/bulk-jobs`);
  return z.array(bulkLeadJobSchema).parse(await response.json());
}

export async function fetchAdminUsers(): Promise<AdminUsersResponse> {
  const response = await authFetch(`${bffBase}/admin/users`);
  return adminUsersResponseSchema.parse(await response.json());
}

export async function deleteAdminUser(userID: string): Promise<void> {
  await authFetch(`${bffBase}/admin/users/${encodeURIComponent(userID)}?confirm=true`, {
    method: "DELETE"
  });
}

export async function bulkDeleteAdminUsers(userIDs: string[], confirmPhrase: string): Promise<void> {
  await authFetch(`${bffBase}/admin/users/bulk-delete`, {
    method: "POST",
    body: JSON.stringify({
      ids: userIDs,
      confirm_phrase: confirmPhrase
    })
  });
}

export async function listWorkspaceMVPOffers(filter: WorkspaceMVPOfferFilter = {}): Promise<WorkspaceMVPOffer[]> {
  return listWorkspaceMVPItems("offers", workspaceMVPOffersResponseSchema, filter);
}

export async function getWorkspaceMVPOffer(id: string): Promise<WorkspaceMVPOffer> {
  return getWorkspaceMVPItem("offers", id, workspaceMVPOfferSchema);
}

export async function createWorkspaceMVPOffer(payload: WorkspaceMVPOfferPayload): Promise<WorkspaceMVPOffer> {
  return createWorkspaceMVPItem("offers", payload, workspaceMVPOfferSchema);
}

export async function updateWorkspaceMVPOffer(
  id: string,
  payload: WorkspaceMVPOfferPayload
): Promise<WorkspaceMVPOffer> {
  return updateWorkspaceMVPItem("offers", id, payload, workspaceMVPOfferSchema);
}

export async function deleteWorkspaceMVPOffer(id: string): Promise<void> {
  return deleteWorkspaceMVPItem("offers", id);
}

export async function listWorkspaceMVPFlows(filter: WorkspaceMVPFlowFilter = {}): Promise<WorkspaceMVPFlow[]> {
  return listWorkspaceMVPItems("flows", workspaceMVPFlowsResponseSchema, filter);
}

export async function getWorkspaceMVPFlow(id: string): Promise<WorkspaceMVPFlow> {
  return getWorkspaceMVPItem("flows", id, workspaceMVPFlowSchema);
}

export async function createWorkspaceMVPFlow(payload: WorkspaceMVPFlowPayload): Promise<WorkspaceMVPFlow> {
  return createWorkspaceMVPItem("flows", payload, workspaceMVPFlowSchema);
}

export async function updateWorkspaceMVPFlow(id: string, payload: WorkspaceMVPFlowPayload): Promise<WorkspaceMVPFlow> {
  return updateWorkspaceMVPItem("flows", id, payload, workspaceMVPFlowSchema);
}

export async function deleteWorkspaceMVPFlow(id: string): Promise<void> {
  return deleteWorkspaceMVPItem("flows", id);
}

export async function listWorkspaceMVPLeads(filter: WorkspaceMVPLeadFilter = {}): Promise<WorkspaceMVPLead[]> {
  return listWorkspaceMVPItems("leads", workspaceMVPLeadsResponseSchema, filter);
}

export async function getWorkspaceMVPLead(id: string): Promise<WorkspaceMVPLead> {
  return getWorkspaceMVPItem("leads", id, workspaceMVPLeadSchema);
}

export async function createWorkspaceMVPLead(payload: WorkspaceMVPLeadPayload): Promise<WorkspaceMVPLead> {
  return createWorkspaceMVPItem("leads", payload, workspaceMVPLeadSchema);
}

export async function updateWorkspaceMVPLead(id: string, payload: WorkspaceMVPLeadPayload): Promise<WorkspaceMVPLead> {
  return updateWorkspaceMVPItem("leads", id, payload, workspaceMVPLeadSchema);
}

export async function deleteWorkspaceMVPLead(id: string): Promise<void> {
  return deleteWorkspaceMVPItem("leads", id);
}

export async function listWorkspaceMVPOrders(filter: WorkspaceMVPOrderFilter = {}): Promise<WorkspaceMVPOrder[]> {
  return listWorkspaceMVPItems("orders", workspaceMVPOrdersResponseSchema, filter);
}

export async function getWorkspaceMVPOrder(id: string): Promise<WorkspaceMVPOrder> {
  return getWorkspaceMVPItem("orders", id, workspaceMVPOrderSchema);
}

export async function createWorkspaceMVPOrder(payload: WorkspaceMVPOrderPayload): Promise<WorkspaceMVPOrder> {
  return createWorkspaceMVPItem("orders", payload, workspaceMVPOrderSchema);
}

export async function updateWorkspaceMVPOrder(
  id: string,
  payload: WorkspaceMVPOrderPayload
): Promise<WorkspaceMVPOrder> {
  return updateWorkspaceMVPItem("orders", id, payload, workspaceMVPOrderSchema);
}

export async function deleteWorkspaceMVPOrder(id: string): Promise<void> {
  return deleteWorkspaceMVPItem("orders", id);
}

export async function listWorkspaceMVPPostbacks(
  filter: WorkspaceMVPPostbackFilter = {}
): Promise<WorkspaceMVPPostback[]> {
  return listWorkspaceMVPItems("postbacks", workspaceMVPPostbacksResponseSchema, filter);
}

export async function getWorkspaceMVPPostback(id: string): Promise<WorkspaceMVPPostback> {
  return getWorkspaceMVPItem("postbacks", id, workspaceMVPPostbackSchema);
}

export async function createWorkspaceMVPPostback(
  payload: WorkspaceMVPPostbackPayload
): Promise<WorkspaceMVPPostback> {
  return createWorkspaceMVPItem("postbacks", payload, workspaceMVPPostbackSchema);
}

export async function updateWorkspaceMVPPostback(
  id: string,
  payload: WorkspaceMVPPostbackPayload
): Promise<WorkspaceMVPPostback> {
  return updateWorkspaceMVPItem("postbacks", id, payload, workspaceMVPPostbackSchema);
}

export async function deleteWorkspaceMVPPostback(id: string): Promise<void> {
  return deleteWorkspaceMVPItem("postbacks", id);
}

export async function listWorkspaceMVPPayouts(filter: WorkspaceMVPPayoutFilter = {}): Promise<WorkspaceMVPPayout[]> {
  return listWorkspaceMVPItems("payouts", workspaceMVPPayoutsResponseSchema, filter);
}

export async function getWorkspaceMVPPayout(id: string): Promise<WorkspaceMVPPayout> {
  return getWorkspaceMVPItem("payouts", id, workspaceMVPPayoutSchema);
}

export async function createWorkspaceMVPPayout(payload: WorkspaceMVPPayoutPayload): Promise<WorkspaceMVPPayout> {
  return createWorkspaceMVPItem("payouts", payload, workspaceMVPPayoutSchema);
}

export async function updateWorkspaceMVPPayout(
  id: string,
  payload: WorkspaceMVPPayoutPayload
): Promise<WorkspaceMVPPayout> {
  return updateWorkspaceMVPItem("payouts", id, payload, workspaceMVPPayoutSchema);
}

export async function deleteWorkspaceMVPPayout(id: string): Promise<void> {
  return deleteWorkspaceMVPItem("payouts", id);
}

export async function listWorkspaceMVPUsers(filter: WorkspaceMVPUserFilter = {}): Promise<WorkspaceMVPUser[]> {
  return listWorkspaceMVPItems("users", workspaceMVPUsersResponseSchema, filter);
}

export async function getWorkspaceMVPUser(id: string): Promise<WorkspaceMVPUser> {
  return getWorkspaceMVPItem("users", id, workspaceMVPUserSchema);
}

export async function createWorkspaceMVPUser(payload: WorkspaceMVPUserPayload): Promise<WorkspaceMVPUser> {
  return createWorkspaceMVPItem("users", payload, workspaceMVPUserSchema);
}

export async function updateWorkspaceMVPUser(id: string, payload: WorkspaceMVPUserPayload): Promise<WorkspaceMVPUser> {
  return updateWorkspaceMVPItem("users", id, payload, workspaceMVPUserSchema);
}

export async function deleteWorkspaceMVPUser(id: string): Promise<void> {
  return deleteWorkspaceMVPItem("users", id);
}

export async function listWorkspaceMVPCompanies(
  filter: WorkspaceMVPCompanyFilter = {}
): Promise<WorkspaceMVPCompany[]> {
  return listWorkspaceMVPItems("companies", workspaceMVPCompaniesResponseSchema, filter);
}

export async function getWorkspaceMVPCompany(id: string): Promise<WorkspaceMVPCompany> {
  return getWorkspaceMVPItem("companies", id, workspaceMVPCompanySchema);
}

export async function createWorkspaceMVPCompany(payload: WorkspaceMVPCompanyPayload): Promise<WorkspaceMVPCompany> {
  return createWorkspaceMVPItem("companies", payload, workspaceMVPCompanySchema);
}

export async function updateWorkspaceMVPCompany(
  id: string,
  payload: WorkspaceMVPCompanyPayload
): Promise<WorkspaceMVPCompany> {
  return updateWorkspaceMVPItem("companies", id, payload, workspaceMVPCompanySchema);
}

export async function deleteWorkspaceMVPCompany(id: string): Promise<void> {
  return deleteWorkspaceMVPItem("companies", id);
}

export async function fetchWorkspaceMVPStats(): Promise<WorkspaceMVPStats> {
  const response = await authFetch(`${workspaceMVPBase}/stats`);
  return workspaceMVPStatsSchema.parse(await response.json());
}

async function postPublicJSON(path: string, payload: unknown): Promise<unknown> {
  const response = await fetch(path, {
    method: "POST",
    headers: {
      "Content-Type": "application/json"
    },
    body: JSON.stringify(payload)
  });
  if (!response.ok) {
    throw new Error(await responseMessage(response));
  }
  return response.json();
}

async function listWorkspaceMVPItems<T>(
  resource: string,
  schema: z.ZodType<{ items: T[] }>,
  filter: Record<string, string | undefined>
): Promise<T[]> {
  const response = await authFetch(`${workspaceMVPBase}/${resource}${queryString(filter)}`);
  return schema.parse(await response.json()).items;
}

async function getWorkspaceMVPItem<T>(resource: string, id: string, schema: z.ZodType<T>): Promise<T> {
  const response = await authFetch(`${workspaceMVPBase}/${resource}/${encodeURIComponent(id)}`);
  return schema.parse(await response.json());
}

async function createWorkspaceMVPItem<T>(
  resource: string,
  payload: unknown,
  schema: z.ZodType<T>
): Promise<T> {
  const response = await authFetch(`${workspaceMVPBase}/${resource}`, {
    method: "POST",
    body: JSON.stringify(payload)
  });
  return schema.parse(await response.json());
}

async function updateWorkspaceMVPItem<T>(
  resource: string,
  id: string,
  payload: unknown,
  schema: z.ZodType<T>
): Promise<T> {
  const response = await authFetch(`${workspaceMVPBase}/${resource}/${encodeURIComponent(id)}`, {
    method: "PUT",
    body: JSON.stringify(payload)
  });
  return schema.parse(await response.json());
}

async function deleteWorkspaceMVPItem(resource: string, id: string): Promise<void> {
  await authFetch(`${workspaceMVPBase}/${resource}/${encodeURIComponent(id)}`, {
    method: "DELETE"
  });
}

function queryString(filter: Record<string, string | undefined>): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(filter)) {
    if (value !== undefined && value !== "") {
      params.set(key, value);
    }
  }
  const serialized = params.toString();
  return serialized ? `?${serialized}` : "";
}

async function authFetch(path: string, init: RequestInit = {}): Promise<Response> {
  const session = await ensureFreshSession();
  if (!session) {
    throw new Error("Authorization required");
  }
  const response = await fetch(path, {
    ...init,
    headers: {
      ...actorHeaders(),
      Authorization: `${session.tokenType} ${session.accessToken}`,
      ...(init.body ? { "Content-Type": "application/json" } : {}),
      ...init.headers
    }
  });
  if (response.status === 401) {
    clearSession();
    throw new Error("Authorization required");
  }
  if (!response.ok) {
    throw new Error(await responseMessage(response));
  }
  return response;
}

async function ensureFreshSession(): Promise<Session | null> {
  const session = loadSession();
  if (!session) {
    return null;
  }
  if (Date.parse(session.accessExpiresAt) - 30_000 > Date.now()) {
    return session;
  }
  try {
    const refreshed = sessionResponseSchema.parse(
      await postPublicJSON("/bff/api/v1/auth/session/refresh", {
        refresh_token: session.refreshToken
      })
    );
    return persistSession({
      ...session,
      accessToken: refreshed.access_token,
      accessExpiresAt: refreshed.access_expires_at,
      refreshToken: refreshed.refresh_token,
      refreshKID: refreshed.refresh_kid,
      refreshExpiresAt: refreshed.refresh_expires_at,
      tokenType: refreshed.token_type
    });
  } catch {
    clearSession();
    return null;
  }
}

async function responseMessage(response: Response): Promise<string> {
  try {
    const body = z.object({ message: z.string().optional() }).passthrough().parse(await response.json());
    return body.message || `Request failed with status ${response.status}`;
  } catch {
    return `Request failed with status ${response.status}`;
  }
}

function actorHeaders(): HeadersInit {
  return {};
}
